import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import {
  GuestCategory,
  getGuestCategories,
  createGuestCategory,
  updateGuestCategory,
  deleteGuestCategory,
} from '../../services/guestCategories';

const PILL_COLORS = [
  '#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E',
  '#C26A6A', '#8E7CC3', '#76A5AF', '#CC7832', '#93C47D',
  '#E06666', '#F6B26B', '#A4C2F4', '#B4A7D6', '#D5A6BD',
];

interface CategoryManagerProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ open, onClose, onChanged }) => {
  const [level1, setLevel1] = useState<GuestCategory[]>([]);
  const [level2, setLevel2] = useState<GuestCategory[]>([]);
  const [newName, setNewName] = useState('');
  const [addingLevel, setAddingLevel] = useState<1 | 2 | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [colorPickerId, setColorPickerId] = useState<string | null>(null);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      getGuestCategories({ level: 1 }),
      getGuestCategories({ level: 2 }),
    ]);
    setLevel1(r1.data);
    setLevel2(r2.data);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleAdd = async (level: 1 | 2) => {
    if (!newName.trim()) return;
    await createGuestCategory({ name: newName.trim(), level });
    setNewName('');
    setAddingLevel(null);
    load();
    onChanged();
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    await updateGuestCategory(id, { name: editName.trim() });
    setEditingId(null);
    setEditName('');
    load();
    onChanged();
  };

  const handleColorChange = async (id: string, color: string) => {
    await updateGuestCategory(id, { color });
    setColorPickerId(null);
    load();
    onChanged();
  };

  const handleDelete = async (id: string) => {
    await deleteGuestCategory(id);
    load();
    onChanged();
  };

  const renderCategorySection = (title: string, cats: GuestCategory[], level: 1 | 2) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem' }}>{title}</Typography>
        <Button
          size="small"
          startIcon={<AppIcon name="add" />}
          onClick={() => { setAddingLevel(addingLevel === level ? null : level); setNewName(''); }}
        >
          Add
        </Button>
      </Box>
      <List disablePadding>
        {cats.map((cat) => (
          <Box key={cat.id}>
            <ListItem
              sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 0.5, border: '1px solid', borderColor: 'grey.200' }}
              secondaryAction={
                <Box>
                  <Tooltip title="Change color">
                    <IconButton size="small" onClick={() => setColorPickerId(colorPickerId === cat.id ? null : cat.id)}>
                      <AppIcon name="color-lens" fontSize="small" sx={{ color: cat.color || 'grey.400' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}>
                      <AppIcon name="edit" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(cat.id)}>
                      <AppIcon name="delete" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              {editingId === cat.id ? (
                <TextField
                  size="small"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdate(cat.id)}
                  onBlur={() => handleUpdate(cat.id)}
                  autoFocus
                />
              ) : (
                <ListItemText
                  primary={
                    <Chip
                      label={cat.name}
                      size="small"
                      sx={{
                        bgcolor: cat.color ? `${cat.color}25` : 'grey.100',
                        color: cat.color || 'grey.800',
                        fontWeight: 500,
                        border: `1px solid ${cat.color || '#E4DED8'}`,
                      }}
                    />
                  }
                />
              )}
            </ListItem>
            {colorPickerId === cat.id && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 2, pb: 1 }}>
                {PILL_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleColorChange(cat.id, color)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: color,
                      cursor: 'pointer',
                      border: cat.color === color ? '2px solid #3D3731' : '2px solid transparent',
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'scale(1.2)' },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </List>
      {addingLevel === level && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            placeholder={`New ${title.toLowerCase().slice(0, -3)} name`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd(level)}
            autoFocus
            fullWidth
          />
          <Button size="small" variant="contained" onClick={() => handleAdd(level)}>
            Add
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Guest Categories</DialogTitle>
      <DialogContent>
        {renderCategorySection('Categories', level1, 1)}
        {renderCategorySection('Sub Categories', level2, 2)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryManager;
